import { Divider, Flex, Group, ScrollArea, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useFlowByPhaseTabStore, useStrategyStore } from '../../../../../store/strategy-store/strategy-store';
import {
  setFlowByPhaseFilterHideEmpty,
  setFlowByPhaseFilterSearchTerm,
  setFlowByPhaseFilterSortBy,
  setFlowByPhaseFilterSortDirection,
  setFlowByPhaseSelectedEpic,
} from '../../../../../store/strategy-store/strategy-store.actions';
import { inkLight, secondaryBase } from '../../../../../styles/design-tokens';
import { Radio } from '../../../../../ui-library/radio/radio';
import { Text } from '../../../../../ui-library/typography/typography';
import { SmallSquareButton } from '../../../../../components/small-square-button/small-square-button';
import { FilterListControls } from '../../components/filters-list/filters-list-controls';
import { getFilterOptions, getSortOptions, isEnabledEpicSearch } from '../../initiative-performance.helpers';
import { EpicSortOption } from '../../initiative-performance.type';
import { useFilteredEpics } from './filters.hooks';

export const Filters = ({ setShow }: { setShow: (show: boolean) => void }) => {
  const i18n = useTranslation();
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  const searchTerm = useFlowByPhaseTabStore((state) => state.filterControls.searchTerm);
  const sortBy = useFlowByPhaseTabStore((state) => state.filterControls.sortBy);
  const sortDirection = useFlowByPhaseTabStore((state) => state.filterControls.sortDirection);
  const hideEmpty = useFlowByPhaseTabStore((state) => state.filterControls.hideEmpty);
  const selectedEpic = useFlowByPhaseTabStore((state) => state.selectedEpic);

  const epics = useFilteredEpics();

  return (
    <Stack gap="md" h="100%">
      <Flex justify="space-between" align="center" w="100%">
        <Text size="medium" weight="bold">
          {i18n.t('strategy.initiative_performance.initiative_epics', {
            initiative: i18n.t('strategy.initiatives.initiative'),
            epics: i18n.t('strategy.initiatives.epics'),
          })}
        </Text>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Flex>
      <Divider />
      {isEnabledEpicSearch(initiative?.epics) && (
        <>
          <FilterListControls
            searchTerm={searchTerm}
            onSearchChange={(value) => setFlowByPhaseFilterSearchTerm(value)}
            searchPlaceholder={i18n.t('strategy.filters.search_by_epic')}
            sortOptions={getSortOptions(i18n.t)}
            selectedSort={sortBy}
            onSortChange={(sortOption: string) => setFlowByPhaseFilterSortBy(sortOption as EpicSortOption)}
            sortDirection={sortDirection}
            onSortDirectionChange={(direction) => setFlowByPhaseFilterSortDirection(direction)}
            filterOptions={getFilterOptions(i18n.t)}
            selectedFilters={{ hideEmpty }}
            onFilterChange={(filterOption: string, value: boolean) => {
              if (filterOption === 'hideEmpty') {
                setFlowByPhaseFilterHideEmpty(value);
              }
            }}
          />
          <Divider />
        </>
      )}
      <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
        <Stack>
          {epics.length > 0 && (
            <>
              <Group justify="space-between">
                <Radio
                  key="all-epics"
                  label={<Text size="small">{i18n.t('strategy.filters.all_epics')}</Text>}
                  checked={selectedEpic === null}
                  onChange={() => setFlowByPhaseSelectedEpic(null)}
                />
                <Text size="small" color={inkLight}>
                  {epics.length}
                </Text>
              </Group>
              <Divider />
            </>
          )}
          <Stack gap={24}>
            {epics.map((epic) => (
              <Stack gap={8} key={epic.id}>
                <Group justify="space-between">
                  <Radio
                    label={
                      <Text size="small" color={secondaryBase} style={{ lineHeight: '20px' }}>
                        {epic.title}
                      </Text>
                    }
                    checked={epic.id === selectedEpic}
                    onChange={() => setFlowByPhaseSelectedEpic(epic.id)}
                  />
                  <Text size="small" color={inkLight}>
                    {`${epic.subtasks_count} ${(epic.subtasks_count === 1 ? i18n.t('common.work_unit') : i18n.t('common.work_units')).toLowerCase()}`}
                  </Text>
                </Group>
                <Text size="small">{epic.name}</Text>
              </Stack>
            ))}
          </Stack>
        </Stack>
        {epics.length === 0 && (
          <Text size="small" color="dimmed">
            {i18n.t('strategy.filters.no_epics_found')}
          </Text>
        )}
      </ScrollArea>
    </Stack>
  );
};
